import { useEffect, useState } from "react";
import { ArrowRight, ArrowDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { imgUrl } from "../lib/format";

/**
 * Cinematic Hero Slideshow — 5 curated luxury villa exteriors crossfading every
 * ~6 seconds. NO property labels, NO prices, NO badges. Just the brand
 * headline + CTAs over rotating editorial photos (Aman-style).
 *
 * Image sources are hardcoded to verified, stable URLs (Pexels CDN + a known
 * Unsplash villa). Pexels direct image URLs do NOT rotate content like
 * Unsplash random endpoints, so the look stays consistent.
 */
// Each URL has been visually verified (via analyze_file_tool) to be a luxury
// villa EXTERIOR — no interiors, no suburban houses, no irrelevant content.
const HERO_SLIDES = [
  // Modern villa with wooden ceiling + infinity pool
  "https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=2400&q=82&auto=format&fit=crop",
  // Contemporary multi-story villa with pool & frameless glass balconies
  "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=2400&q=82&auto=format&fit=crop",
  // Elegant villa with manicured garden, palm trees & free-form pool
  "https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=2400&q=82&auto=format&fit=crop",
  // Modern multi-level villa with dark wood cladding & mature garden tree
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=2400&q=82&auto=format&fit=crop",
  // Minimalist white villa with infinity pool & wooden deck
  "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=2400&q=82&auto=format&fit=crop",
];

const SLIDE_DURATION_MS = 6500;

export default function Hero() {
  const { t } = useTranslation();
  const [index, setIndex] = useState(0);

  const resolveSrc = (src, w) => imgUrl(src, w) || src;

  // Mount the remaining slides only AFTER first paint (2.5s) so phones don't
  // download 5 full-screen photos while the page is still loading. The first
  // crossfade happens at 6.5s, so slides are always ready in time.
  const [warm, setWarm] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setWarm(true), 2500);
    return () => clearTimeout(id);
  }, []);

  // Auto-advance the slideshow (respects prefers-reduced-motion).
  useEffect(() => {
    const reduce = typeof window !== "undefined"
      && window.matchMedia
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;

    const id = window.setInterval(() => {
      setIndex((i) => (i + 1) % HERO_SLIDES.length);
    }, SLIDE_DURATION_MS);
    return () => window.clearInterval(id);
  }, []);

  return (
    <section
      id="accueil"
      data-testid="hero-section"
      className="relative w-full h-[100svh] min-h-[640px] max-h-[1080px] overflow-hidden bg-[#1A1A1A]"
    >
      {/* Crossfading full-bleed slideshow */}
      <div className="absolute inset-0">
        {HERO_SLIDES.map((src, i) => (
          (i === 0 || warm) && (
          <img
            key={src}
            src={resolveSrc(src, 1600)}
            srcSet={`${resolveSrc(src, 800)} 800w, ${resolveSrc(src, 1200)} 1200w, ${resolveSrc(src, 1600)} 1600w, ${resolveSrc(src, 2400)} 2400w`}
            sizes="100vw"
            alt=""
            aria-hidden="true"
            data-testid={`hero-slide-${i}`}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1600ms] ease-in-out ${
              i === index ? "opacity-100 hero-zoom-out" : "opacity-0"
            }`}
            loading={i === 0 ? "eager" : "lazy"}
            fetchPriority={i === 0 ? "high" : "low"}
            decoding="async"
          />
          )
        ))}
      </div>

      {/* Cinematic gradient — keeps the bottom-left headline readable */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.05) 25%, rgba(0,0,0,0) 45%, rgba(0,0,0,0.55) 70%, rgba(0,0,0,0.92) 100%), linear-gradient(105deg, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.40) 30%, rgba(0,0,0,0.15) 55%, rgba(0,0,0,0) 80%)",
        }}
        aria-hidden="true"
      />

      {/* Headline + CTAs — editorial overlay pinned to the bottom-left */}
      <div className="absolute inset-x-0 bottom-0 z-10 pb-24 sm:pb-20 md:pb-28 lg:pb-32">
        <div className="mx-auto max-w-[1480px] px-4 sm:px-6 md:px-12 lg:px-20">
          <div className="max-w-[820px] text-white">
            <div className="flex items-center gap-3 mb-5 md:mb-6 reveal in">
              <span className="block w-10 md:w-12 h-px bg-[#D4B58C]" />
              <span className="text-[10px] md:text-[11px] tracking-[0.24em] uppercase font-semibold text-[#D4B58C]">
                {t("hero.tagline")}
              </span>
            </div>

            <h1 className="font-serif text-[30px] sm:text-[44px] md:text-[60px] lg:text-[80px] xl:text-[92px] leading-[1.02] sm:leading-[1.0] tracking-[-0.02em] sm:tracking-[-0.025em] font-medium drop-shadow-[0_2px_4px_rgba(0,0,0,0.85)] [text-shadow:0_4px_30px_rgba(0,0,0,0.65),0_0_2px_rgba(0,0,0,0.4)]">
              <span className="block reveal in" style={{ transitionDelay: "120ms" }}>
                {t("hero.title_l1")}
              </span>
              <span
                className="block italic text-[#D4B58C] font-light reveal in"
                style={{ transitionDelay: "260ms" }}
              >
                {t("hero.title_l2")}
              </span>
            </h1>

            <p
              className="mt-6 md:mt-8 text-white/90 text-sm sm:text-base md:text-lg max-w-[560px] leading-relaxed drop-shadow-[0_2px_12px_rgba(0,0,0,0.7)] reveal in"
              style={{ transitionDelay: "420ms" }}
            >
              {t("hero.subtitle")}
            </p>

            <div
              className="mt-8 md:mt-10 flex flex-wrap gap-3 reveal in"
              style={{ transitionDelay: "560ms" }}
            >
              <a href="#biens" className="btn-primary btn-gold" data-testid="hero-discover-btn">
                {t("hero.cta_explore")} <ArrowRight size={16} strokeWidth={1.5} />
              </a>
              <a
                href="#services"
                className="btn-outline"
                style={{ color: "#fff", borderColor: "#fff" }}
                data-testid="hero-services-btn"
              >
                {t("nav.services")}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Slide indicator dots (discreet, bottom-right) */}
      <div
        className="absolute bottom-6 right-6 md:bottom-8 md:right-10 z-10 flex items-center gap-2"
        data-testid="hero-slide-dots"
        aria-hidden="true"
      >
        {HERO_SLIDES.map((_, i) => (
          <span
            key={i}
            className={`block h-px transition-all duration-500 ${
              i === index ? "w-8 bg-[#D4B58C]" : "w-4 bg-white/40"
            }`}
          />
        ))}
      </div>

      <a
        href="#biens"
        className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex-col items-center gap-2 text-white/70 hover:text-white transition-colors"
        aria-label="Faire défiler"
      >
        <span className="text-[10px] tracking-[0.28em] uppercase font-semibold">Découvrir</span>
        <ArrowDown size={16} strokeWidth={1.5} className="animate-bounce" />
      </a>
    </section>
  );
}
